"use client";

import { useState, useEffect } from "react";
import {
  AppBar,
  Toolbar,
  IconButton,
  InputBase,
  Drawer,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Box,
  Badge,
  Paper,
  CircularProgress,
  MenuItem,
  useMediaQuery,
  Typography,
  Divider,
  Avatar,
  Menu,
  Chip,
  Container,
  Fade,
} from "@mui/material";
import { styled, useTheme } from "@mui/material/styles";
import SearchIcon from "@mui/icons-material/Search";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import LocalMallIcon from "@mui/icons-material/LocalMall";
import HomeIcon from "@mui/icons-material/Home";
import AccountCircle from "@mui/icons-material/AccountCircle";
import MenuIcon from "@mui/icons-material/Menu";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import PersonIcon from "@mui/icons-material/Person";
import LogoutIcon from "@mui/icons-material/Logout";
import FavoriteIcon from "@mui/icons-material/Favorite";
import SettingsIcon from "@mui/icons-material/Settings";
import CloseIcon from "@mui/icons-material/Close";
import { Link, useNavigate } from "react-router-dom";
import Logo from "../../assets/images/logo1.png";
import debounce from "lodash.debounce";

// Enhanced styled components
const StyledAppBar = styled(AppBar)(({ theme }) => ({
  background: "linear-gradient(135deg, #008080 0%, #20B2AA 100%)",
  boxShadow: "0 4px 20px rgba(0, 128, 128, 0.3)",
  backdropFilter: "blur(10px)",
  borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
}));

const SearchContainer = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  backgroundColor: "rgba(255, 255, 255, 0.95)",
  backdropFilter: "blur(10px)",
  boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)",
  width: "100%",
  maxWidth: 600,
  borderRadius: "25px",
  marginLeft: theme.spacing(2),
  marginRight: theme.spacing(2),
  position: "relative",
  height: "48px",
  border: "2px solid transparent",
  transition: "all 0.3s ease",
  "&:hover": {
    boxShadow: "0 6px 25px rgba(0, 128, 128, 0.2)",
    border: "2px solid rgba(0, 128, 128, 0.3)",
  },
  "&:focus-within": {
    border: "2px solid #008080",
    boxShadow: "0 6px 25px rgba(0, 128, 128, 0.3)",
  },
}));

const SearchInput = styled(InputBase)(({ theme }) => ({
  flexGrow: 1,
  padding: theme.spacing(1, 2),
  backgroundColor: "transparent",
  fontSize: "0.95rem",
  fontWeight: 500,
  "& input": {
    "&::placeholder": {
      color: "rgba(0, 0, 0, 0.6)",
      opacity: 1,
    },
  },
}));

const SearchButton = styled(IconButton)(({ theme }) => ({
  padding: theme.spacing(1.2),
  color: "#008080",
  backgroundColor: "rgba(0, 128, 128, 0.1)",
  borderRadius: "50%",
  margin: theme.spacing(0.5),
  transition: "all 0.3s ease",
  "&:hover": {
    backgroundColor: "#008080",
    color: "white",
    transform: "scale(1.05)",
  },
}));

const SuggestionsContainer = styled(Paper)(({ theme }) => ({
  position: "absolute",
  top: "100%",
  left: 0,
  right: 0,
  zIndex: 10,
  backgroundColor: "rgba(255, 255, 255, 0.98)",
  backdropFilter: "blur(20px)",
  boxShadow: "0 8px 32px rgba(0, 0, 0, 0.15)",
  maxHeight: 400,
  overflowY: "auto",
  borderRadius: "16px",
  marginTop: theme.spacing(1),
  border: "1px solid rgba(0, 128, 128, 0.2)",
}));

const NavButton = styled(IconButton)(({ theme }) => ({
  color: "white",
  padding: theme.spacing(1),
  borderRadius: "12px",
  margin: theme.spacing(0, 0.5),
  transition: "all 0.3s ease",
  "&:hover": {
    backgroundColor: "rgba(255, 255, 255, 0.15)",
    transform: "translateY(-2px)",
  },
  "& .nav-text": {
    fontSize: "0.9rem",
    fontWeight: 600,
    marginLeft: theme.spacing(0.5),
    [theme.breakpoints.down("lg")]: {
      display: "none",
    },
  },
}));

const CartBadge = styled(Badge)(({ theme }) => ({
  "& .MuiBadge-badge": {
    backgroundColor: "#FF4444",
    color: "white",
    fontWeight: "bold",
    fontSize: "0.75rem",
    minWidth: "20px",
    height: "20px",
    animation: "pulse 2s infinite",
  },
  "@keyframes pulse": {
    "0%": {
      transform: "scale(1)",
    },
    "50%": {
      transform: "scale(1.1)",
    },
    "100%": {
      transform: "scale(1)",
    },
  },
}));

const UserChip = styled(Chip)(({ theme }) => ({
  backgroundColor: "rgba(255, 255, 255, 0.2)",
  color: "white",
  fontWeight: 600,
  padding: theme.spacing(0.5, 1),
  "&:hover": {
    backgroundColor: "rgba(255, 255, 255, 0.3)",
  },
  "& .MuiChip-avatar": {
    backgroundColor: "white",
    color: "#008080",
  },
}));

const MobileDrawer = styled(Drawer)(({ theme }) => ({
  "& .MuiDrawer-paper": {
    width: 280,
    background: "linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)",
    borderTopRightRadius: "20px",
    borderBottomRightRadius: "20px",
  },
}));

function Navbar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [cartItemCount, setCartItemCount] = useState(0);
  const [username, setUsername] = useState(null);
  const [userMenuAnchor, setUserMenuAnchor] = useState(null);
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isTablet = useMediaQuery(theme.breakpoints.down("lg"));

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleSearchToggle = () => {
    setSearchOpen(!searchOpen);
  };

  const handleUserMenuOpen = (event) => {
    setUserMenuAnchor(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setUserMenuAnchor(null);
  };

  useEffect(() => {
    if (query.length > 2) {
      debouncedFetchSuggestions(query);
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }
  }, [query]);

  const debouncedFetchSuggestions = debounce(async (query) => {
    try {
      setLoading(true);
      const response = await fetch(
        `https://namami-infotech.com/EvaraBackend/src/sku/list_sku.php?alpha=${query}`
      );
      const data = await response.json();
      const filteredSuggestions = data.medicines
        ? data.medicines.filter((medicine) => medicine.status === 1)
        : [];
      setSuggestions(filteredSuggestions);
      setShowSuggestions(true);
    } catch (error) {
      console.error("Error fetching suggestions:", error);
    } finally {
      setLoading(false);
    }
  }, 300);

  const handleSearch = () => {
    if (!query) return;
    navigate(`/search?query=${query}`);
    setShowSuggestions(false);
    setSearchOpen(false);
  };

  const handleSuggestionClick = (suggestion) => {
    navigate(`/product/${suggestion.medicine_id}`);
    setQuery("");
    setShowSuggestions(false);
    setSearchOpen(false);
  };

  const updateCartItemCount = () => {
    const cart = JSON.parse(localStorage.getItem("cart")) || [];
    const totalItems = cart.reduce((total, item) => total + item.quantity, 0);
    setCartItemCount(totalItems);
  };

  useEffect(() => {
    updateCartItemCount();
    const userData = JSON.parse(localStorage.getItem("userData"));
    if (userData) {
      setUsername(userData.username);
    }
  }, []);

  useEffect(() => {
    const handleStorageChange = () => {
      updateCartItemCount();
      const userData = JSON.parse(localStorage.getItem("userData"));
      if (userData) {
        setUsername(userData.username);
      } else {
        setUsername(null);
      }
    };
    window.addEventListener("storage", handleStorageChange);
    return () => {
      window.removeEventListener("storage", handleStorageChange);
    };
  }, []);

  const drawer = (
    <Box sx={{ width: 280, pt: 2 }}>
      <Box sx={{ display: "flex", alignItems: "center", px: 2, pb: 2 }}>
        <img
          src={Logo || "/placeholder.svg"}
          alt="Logo"
          style={{ width: 50, height: 50 }}
        />
        <Typography
          variant="h6"
          sx={{ ml: 1, fontWeight: "bold", color: "#008080" }}
        >
          EVARA
        </Typography>
      </Box>
      <Divider />
      <List sx={{ pt: 2 }}>
        {[
          { text: "Home", icon: <HomeIcon />, path: "/" },
          { text: "Orders", icon: <LocalMallIcon />, path: "/orders" },
          { text: "Support", icon: <SupportAgentIcon />, path: "/support" },
          { text: "Wishlist", icon: <FavoriteIcon />, path: "/wishlist" },
        ].map((item) => (
          <ListItem
            key={item.text}
            component={Link}
            to={item.path}
            onClick={handleDrawerToggle}
            sx={{
              borderRadius: "12px",
              mx: 1,
              mb: 0.5,
              "&:hover": {
                backgroundColor: "rgba(0, 128, 128, 0.1)",
              },
            }}
          >
            <ListItemIcon sx={{ color: "#008080" }}>{item.icon}</ListItemIcon>
            <ListItemText
              primary={item.text}
              primaryTypographyProps={{ fontWeight: 600 }}
            />
          </ListItem>
        ))}
      </List>
      <Divider sx={{ my: 2 }} />
      <Box sx={{ px: 2 }}>
        <SearchContainer sx={{ maxWidth: "100%", mx: 0 }}>
          <SearchInput
            placeholder="Search medicines..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyPress={(e) => e.key === "Enter" && handleSearch()}
          />
          <SearchButton onClick={handleSearch}>
            {loading ? <CircularProgress size={20} /> : <SearchIcon />}
          </SearchButton>
        </SearchContainer>
      </Box>
    </Box>
  );

  return (
    <>
      <StyledAppBar position="fixed">
        <Container maxWidth="xl">
          <Toolbar sx={{ px: { xs: 0, sm: 2 } }}>
            {/* Logo */}
            <Box sx={{ display: "flex", alignItems: "center", mr: 2 }}>
              <Link to="/" style={{ display: "flex", alignItems: "center" }}>
                <img
                  src={Logo || "/placeholder.svg"}
                  alt="Logo"
                  style={{
                    width: isMobile ? 45 : 60,
                    height: isMobile ? 45 : 60,
                    transition: "transform 0.3s ease",
                  }}
                />
                {!isMobile && (
                  <Typography
                    variant="h6"
                    sx={{
                      ml: 1,
                      fontWeight: "bold",
                      color: "white",
                      display: { xs: "none", sm: "block" },
                    }}
                  >
                    EVARA LIFE SCIENCES
                  </Typography>
                )}
              </Link>
            </Box>

            {/* Desktop Search */}
            {!isMobile && (
              <Box
                sx={{
                  flexGrow: 1,
                  display: "flex",
                  justifyContent: "center",
                  mx: 2,
                }}
              >
                <SearchContainer>
                  <SearchInput
                    placeholder="Search for medicines & wellness products..."
                    value={query}
                    onChange={(e) => {
                      setQuery(e.target.value);
                    }}
                    onKeyPress={(e) => e.key === "Enter" && handleSearch()}
                    onFocus={() => query.length > 2 && setShowSuggestions(true)}
                    onBlur={() =>
                      setTimeout(() => setShowSuggestions(false), 200)
                    }
                  />
                  <SearchButton onClick={handleSearch}>
                    {loading ? <CircularProgress size={20} /> : <SearchIcon />}
                  </SearchButton>
                  {showSuggestions && suggestions.length > 0 && (
                    <Fade in={showSuggestions}>
                      <SuggestionsContainer>
                        <List sx={{ py: 1 }}>
                          {suggestions.slice(0, 6).map((suggestion) => (
                            <ListItem
                              key={suggestion.medicine_id}
                              onClick={() => handleSuggestionClick(suggestion)}
                              sx={{
                                cursor: "pointer",
                                borderRadius: "8px",
                                mx: 1,
                                "&:hover": {
                                  backgroundColor: "rgba(0, 128, 128, 0.05)",
                                },
                              }}
                            >
                              <Avatar
                                src={suggestion.image_url}
                                alt={suggestion.name}
                                sx={{ width: 40, height: 40, mr: 2 }}
                              />
                              <ListItemText
                                primary={suggestion.name}
                                primaryTypographyProps={{
                                  fontWeight: 500,
                                  fontSize: "0.9rem",
                                }}
                              />
                            </ListItem>
                          ))}
                        </List>
                      </SuggestionsContainer>
                    </Fade>
                  )}
                </SearchContainer>
              </Box>
            )}

            {/* Desktop Navigation */}
            {!isTablet && (
              <Box sx={{ display: "flex", alignItems: "center", mr: 2 }}>
                <NavButton component={Link} to="/">
                  <HomeIcon />
                  <span className="nav-text">Home</span>
                </NavButton>
                <NavButton component={Link} to="/orders">
                  <LocalMallIcon />
                  <span className="nav-text">Orders</span>
                </NavButton>
                <NavButton component={Link} to="/support">
                  <SupportAgentIcon />
                  <span className="nav-text">Support</span>
                </NavButton>
              </Box>
            )}

            {/* User Section */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {/* Mobile Search Button */}
              {isMobile && (
                <IconButton
                  color="inherit"
                  onClick={handleSearchToggle}
                  sx={{
                    backgroundColor: "rgba(255, 255, 255, 0.1)",
                    "&:hover": { backgroundColor: "rgba(255, 255, 255, 0.2)" },
                  }}
                >
                  <SearchIcon />
                </IconButton>
              )}

              {/* User Menu */}
              {username ? (
                <>
                  <UserChip
                    avatar={
                      <Avatar sx={{ bgcolor: "white", color: "#008080" }}>
                        {username.charAt(0).toUpperCase()}
                      </Avatar>
                    }
                    label={`Hi, ${username}`}
                    onClick={handleUserMenuOpen}
                    clickable
                  />
                  <Menu
                    anchorEl={userMenuAnchor}
                    open={Boolean(userMenuAnchor)}
                    onClose={handleUserMenuClose}
                    PaperProps={{
                      sx: {
                        mt: 1,
                        borderRadius: "12px",
                        boxShadow: "0 8px 32px rgba(0, 0, 0, 0.15)",
                      },
                    }}
                  >
                    <MenuItem
                      component={Link}
                      to="/profile"
                      onClick={handleUserMenuClose}
                    >
                      <PersonIcon sx={{ mr: 1 }} /> Profile
                    </MenuItem>
                    <MenuItem
                      component={Link}
                      to="/settings"
                      onClick={handleUserMenuClose}
                    >
                      <SettingsIcon sx={{ mr: 1 }} /> Settings
                    </MenuItem>
                    <MenuItem onClick={handleUserMenuClose}>
                      <LogoutIcon sx={{ mr: 1 }} /> Logout
                    </MenuItem>
                  </Menu>
                </>
              ) : (
                <NavButton component={Link} to="/login">
                  <AccountCircle />
                  <span className="nav-text">Login</span>
                </NavButton>
              )}

              {/* Cart */}
              <NavButton component={Link} to="/cart">
                <CartBadge badgeContent={cartItemCount} color="error">
                  <ShoppingCartIcon />
                </CartBadge>
                <span className="nav-text">Cart</span>
              </NavButton>

              {/* Mobile Menu Button */}
              {(isMobile || isTablet) && (
                <IconButton
                  color="inherit"
                  onClick={handleDrawerToggle}
                  sx={{
                    ml: 1,
                    backgroundColor: "rgba(255, 255, 255, 0.1)",
                    "&:hover": { backgroundColor: "rgba(255, 255, 255, 0.2)" },
                  }}
                >
                  <MenuIcon />
                </IconButton>
              )}
            </Box>
          </Toolbar>
        </Container>
      </StyledAppBar>

      {/* Spacer */}
      <Toolbar />

      {/* Mobile Drawer */}
      <MobileDrawer
        anchor="left"
        open={mobileOpen}
        onClose={handleDrawerToggle}
      >
        {drawer}
      </MobileDrawer>

      {/* Mobile Search Drawer */}
      <Drawer
        anchor="top"
        open={searchOpen}
        onClose={handleSearchToggle}
        PaperProps={{
          sx: {
            borderBottomLeftRadius: "20px",
            borderBottomRightRadius: "20px",
            background: "linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)",
          },
        }}
      >
        <Box sx={{ p: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
            <Typography
              variant="h6"
              sx={{ flexGrow: 1, fontWeight: "bold", color: "#008080" }}
            >
              Search Products
            </Typography>
            <IconButton onClick={handleSearchToggle}>
              <CloseIcon />
            </IconButton>
          </Box>
          <SearchContainer sx={{ maxWidth: "100%", mx: 0 }}>
            <SearchInput
              placeholder="Search for medicines & wellness products..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyPress={(e) => e.key === "Enter" && handleSearch()}
              autoFocus
            />
            <SearchButton onClick={handleSearch}>
              {loading ? <CircularProgress size={20} /> : <SearchIcon />}
            </SearchButton>
          </SearchContainer>
          {showSuggestions && suggestions.length > 0 && (
            <Paper
              sx={{
                mt: 2,
                borderRadius: "12px",
                maxHeight: 300,
                overflow: "auto",
              }}
            >
              <List>
                {suggestions.slice(0, 5).map((suggestion) => (
                  <ListItem
                    key={suggestion.medicine_id}
                    onClick={() => handleSuggestionClick(suggestion)}
                    sx={{ cursor: "pointer" }}
                  >
                    <Avatar
                      src={suggestion.image_url}
                      alt={suggestion.name}
                      sx={{ width: 40, height: 40, mr: 2 }}
                    />
                    <ListItemText primary={suggestion.name} />
                  </ListItem>
                ))}
              </List>
            </Paper>
          )}
        </Box>
      </Drawer>
    </>
  );
}

export default Navbar;
